import type { IntakeSpec, MechanicalHousingDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';
import { HEAT_SET_DEFAULTS } from '@/lib/intakeBuilderDefaults';
import { METRIC_SCREWS } from '@shared/hardwareCatalog';

function getDetails(intake: IntakeSpec): MechanicalHousingDetails {
  return intake.details as MechanicalHousingDetails;
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const dims = {
    length: intake.core.size?.length ?? 120,
    width: intake.core.size?.width ?? 80,
    height: intake.core.size?.height ?? 60,
  };
  const wallT = 2.5;

  if (d.closure === 'split_clamshell') {
    return [
      {
        id: 'clamshell_bottom', name: 'クラムシェル下半体', kind: 'custom', materialId: 'mat-primary',
        openscadSnippet: '// clamshell half stub',
        boundingBox: { w: dims.length, d: dims.width, h: dims.height / 2 },
        position: [0, 0, 0], rotation: [0, 0, 0], tags: ['clamshell'], paramRefs: [],
      },
      {
        id: 'clamshell_top', name: 'クラムシェル上半体', kind: 'custom', materialId: 'mat-primary',
        openscadSnippet: '// clamshell half stub',
        boundingBox: { w: dims.length, d: dims.width, h: dims.height / 2 },
        position: [0, 0, dims.height / 2], rotation: [0, 0, 0], tags: ['clamshell'], paramRefs: [],
      },
    ];
  }

  // Common parts for lid_screwed, lid_snap_fit, access_panel
  const out: Part[] = [
    {
      id: 'bottom', name: '底板', kind: 'panel', materialId: 'mat-primary',
      width: dims.length, depth: dims.width, thickness: wallT,
      position: [0, 0, 0], rotation: [0, 0, 0], tags: ['bottom'], paramRefs: [], cutouts: [],
    },
    {
      id: 'wall_front', name: '前壁', kind: 'panel', materialId: 'mat-primary',
      width: dims.length, depth: wallT, thickness: dims.height,
      position: [0, -(dims.width / 2), dims.height / 2], rotation: [0, 0, 0], tags: ['wall'], paramRefs: [], cutouts: [],
    },
    {
      id: 'wall_back', name: '後壁', kind: 'panel', materialId: 'mat-primary',
      width: dims.length, depth: wallT, thickness: dims.height,
      position: [0, dims.width / 2, dims.height / 2], rotation: [0, 0, 0], tags: ['wall'], paramRefs: [], cutouts: [],
    },
    {
      id: 'wall_left', name: '左壁', kind: 'panel', materialId: 'mat-primary',
      width: dims.width - 2 * wallT, depth: wallT, thickness: dims.height,
      position: [-(dims.length / 2), 0, dims.height / 2], rotation: [0, 0, 90], tags: ['wall'], paramRefs: [], cutouts: [],
    },
    {
      id: 'wall_right', name: '右壁', kind: 'panel', materialId: 'mat-primary',
      width: dims.width - 2 * wallT, depth: wallT, thickness: dims.height,
      position: [dims.length / 2, 0, dims.height / 2], rotation: [0, 0, 90], tags: ['wall'], paramRefs: [], cutouts: [],
    },
    {
      id: 'lid', name: '蓋', kind: 'panel', materialId: 'mat-primary',
      width: dims.length, depth: dims.width, thickness: wallT,
      position: [0, 0, dims.height], rotation: [0, 0, 0], tags: ['lid'], paramRefs: [], cutouts: [],
    },
  ];

  if (d.closure === 'access_panel') {
    out.push({
      id: 'access_cover', name: 'アクセスカバー', kind: 'panel', materialId: 'mat-primary',
      width: dims.length * 0.4, depth: dims.width * 0.4, thickness: wallT,
      position: [0, 0, dims.height + wallT], rotation: [0, 0, 0], tags: ['access_cover'], paramRefs: [], cutouts: [],
    });
  }

  return out;
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const dims = {
    length: intake.core.size?.length ?? 120,
    width: intake.core.size?.width ?? 80,
    height: intake.core.size?.height ?? 60,
  };
  const wallT = 2.5;
  const out: Feature[] = [];

  if (d.internalFeatures.includes('mounting_bosses')) {
    const heatSet = HEAT_SET_DEFAULTS[d.fastenerSize] ?? HEAT_SET_DEFAULTS['M3'];
    const bossPositions: Array<[number, number, number]> = [
      [-(dims.length / 2 - 10), -(dims.width / 2 - 10), 0],
      [dims.length / 2 - 10, -(dims.width / 2 - 10), 0],
      [-(dims.length / 2 - 10), dims.width / 2 - 10, 0],
      [dims.length / 2 - 10, dims.width / 2 - 10, 0],
    ];
    for (let i = 0; i < 4; i++) {
      out.push({
        id: `boss_${i + 1}`,
        partId: 'bottom',
        kind: 'boss',
        diameter: heatSet.bossOd,
        height: heatSet.bossHeight,
        axis: 'z',
        holeDiameter: heatSet.holeId,
        ribCount: 0,
        position: bossPositions[i],
        rotation: [0, 0, 0],
        paramRefs: [],
      });
    }
  }

  if (d.internalFeatures.includes('cooling_fins')) {
    const ribSpacing = (dims.width - 20) / 3;
    for (let i = 0; i < 4; i++) {
      out.push({
        id: `cooling_fin_${i + 1}`,
        partId: 'bottom',
        kind: 'rib',
        length: dims.length - 20,
        height: 5,
        thickness: 2,
        axis: 'x',
        position: [0, -(dims.width / 2 - 10) + ribSpacing * i, 0],
        rotation: [0, 0, 0],
        paramRefs: [],
      });
    }
  }

  if (d.internalFeatures.includes('vent')) {
    // Use lid as the largest face for lid_screwed, otherwise wall_back
    const ventPartId = d.closure === 'lid_screwed' || d.closure === 'lid_snap_fit' ? 'lid' : 'wall_back';
    out.push({
      id: 'vent_grid',
      partId: ventPartId,
      kind: 'vent_grid',
      rows: 5,
      columns: 8,
      pitchX: 8,
      pitchY: 8,
      holeDiameter: 4,
      depth: wallT,
      axis: 'z',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  // cable_glands: cable_gland feature kind is B2 rest, deferred — emit nothing for now.
  //

  // Lid head-recess pockets for screwed-lid closure at extreme quality.
  if (d.closure === 'lid_screwed' && intake.qualityLevel === 'extreme') {
    const screwCfg = METRIC_SCREWS[d.fastenerSize] ?? METRIC_SCREWS.M3;
    const cornerInset = 8;
    const cornerPositions: Array<[string, [number, number, number]]> = [
      ['fl', [-(dims.length / 2 - cornerInset), -(dims.width / 2 - cornerInset), 0]],
      ['fr', [dims.length / 2 - cornerInset, -(dims.width / 2 - cornerInset), 0]],
      ['rl', [-(dims.length / 2 - cornerInset), dims.width / 2 - cornerInset, 0]],
      ['rr', [dims.length / 2 - cornerInset, dims.width / 2 - cornerInset, 0]],
    ];
    for (const [corner, pos] of cornerPositions) {
      out.push({
        id: `lid-recess-${corner}`,
        partId: 'lid',
        kind: 'pocket',
        shape: 'cylindrical',
        diameter: screwCfg.socketHeadDiameter + 0.5,
        depth: screwCfg.recommendedCounterboreDepth,
        position: pos,
        rotation: [0, 0, 0],
        axis: 'z',
        bottom: 'flat',
        purpose: 'recess',
        catalogSource: `METRIC_SCREWS.${d.fastenerSize}.recommendedCounterboreDepth`,
        callout: { headStyle: 'socket', sectionDetailHint: 'detail-view-needed' },
        paramRefs: [],
      });
    }
  }

  if (intake.qualityLevel === 'extreme') {
    out.push({
      id: 'lid_chamfer',
      partId: 'lid',
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 0.5,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  return out;
}
